import React, { FC, useEffect, useState } from "react"
import { AdminFormFieldInput, AdminFormFieldSubmit } from "../../../../components/admin/formFields";
import { useTranslation } from "react-i18next";
import AddMaintenanceDetails from "./addMaintenanceDetails";
import { useDispatch, useSelector } from "react-redux";
import { setModalsData, TModalsState } from "../../../../api/store/commonSlice";
import { getDistanceByUnit, getLabelByUnit, getRoundedOffValue, MAINTENANCE_ROUND_OFF } from "../../../../utils/distance";
import { DISTANCE_UNIT_VALUES } from "../../account/constants";

interface MaintenanceFormProps {
  label: string;
  maintenanceTypeId: string;
  maintenanceIntervalId: string;
  values: any;
  errors: any;
  touched: any;
  handleChange: (event: React.ChangeEvent<any>) => void;
  formikSetValue: (field: string, value: any, shouldValidate?: boolean) => void;
  handleBlur: (event: React.FocusEvent<any>) => void;
  formikSetTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
  userCanEdit: boolean;
  loadingData: boolean;
  bottomLabel: string;
  distanceUnit?: string;
  reportedValueId?: string;
}

export const MaintenanceInputForm: FC<MaintenanceFormProps> = ({
  label,
  maintenanceTypeId,
  maintenanceIntervalId,
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
  bottomLabel,
  distanceUnit = "km",
  reportedValueId = "reported_odometer",
}) => {
  const { t } = useTranslation("translation", { keyPrefix: "admins.maintenance.detailsPage.form" });
  const dispatch = useDispatch();
  const modalsState = useSelector((state: { commonReducer: { modals: TModalsState } }) => state.commonReducer.modals);

  const [dueValue, setDueValue] = useState(0);
  const [isOverDue, setIsOverDue] = useState("");
  const [remainingValue, setRemainingValue] = useState(0);

  // Update dueValue when values change
  useEffect(() => {
    if (values) {
      // Ensure that maintenanceTypeId and maintenanceIntervalId are defined and valid
      const maintenanceValue = parseFloat(values[maintenanceTypeId]) || 0; // Default to 0 if undefined
      const intervalValue = parseFloat(values[maintenanceIntervalId]) || 0; // Default to 0 if undefined
      setDueValue(parseFloat((maintenanceValue + intervalValue).toFixed(2)));
    }
  }, [values, maintenanceTypeId, maintenanceIntervalId]);

  // Update isOverDue and remainingValue when dueValue or reported_odometer changes
  useEffect(() => {
    const reportedValue = parseFloat(values?.[reportedValueId]) || 0;
    setIsOverDue(dueValue !== 0 && dueValue < reportedValue ? "Maintenance is overdue" : "");
    setRemainingValue(Math.max(0, dueValue - reportedValue));
  }, [dueValue, values?.[reportedValueId]]);
 

  const onInputChange = (e: React.ChangeEvent<any>) => {
    if (isNaN(e.target.value) || (e.target.value.includes(".") && e.target.value.split(".")[1].length > MAINTENANCE_ROUND_OFF)) {
      return;
    }
   
    if (parseFloat(e.target.value) < 0) {
      return;
    }
    handleChange(e);
  }

  return (
    <>
      <div className="col-span-12 grid grid-cols-12 gap-4">

        <AdminFormFieldInput
          label={label}
          type="text"
          id={maintenanceTypeId}
          name={maintenanceTypeId}
          bottomLabel={bottomLabel}
          value={values?.[maintenanceTypeId]}
          onChange={onInputChange}
          onBlur={handleBlur}
          touched={touched?.[maintenanceTypeId]}
          error={errors?.[maintenanceTypeId]}
          disabled={!userCanEdit}
          detailsFormField={true}
          customWrapperClass="col-span-3 self-end"
        />

        <AdminFormFieldInput
          label={t('maintenance_interval')}
          type="text"
          id={maintenanceIntervalId}
          name={maintenanceIntervalId}
          bottomLabel={bottomLabel}
          value={values?.[maintenanceIntervalId]}
          onChange={onInputChange}
          onBlur={handleBlur}
          touched={touched?.[maintenanceIntervalId]}
          error={errors?.[maintenanceIntervalId]}
          disabled={!userCanEdit}
          detailsFormField={true}
          customWrapperClass="col-span-3 self-end"
        />

        <AdminFormFieldInput
          label={t('due')}
          type="text"
          id="due"
          name="due"
          bottomLabel={bottomLabel}
          value={dueValue}
          // onChange={handleChange}
          // onBlur={handleBlur}
          // touched={touched.due}
          error={isOverDue}
          disabled={false}
          detailsFormField={true}
          showErrorValue={false}
          readOnly={true}
          customWrapperClass="col-span-3 self-end"
        />

        <AdminFormFieldInput
          label={t('remaining')}
          type="text"
          id="remaining"
          name="remaining"
          bottomLabel={bottomLabel}
          value={getRoundedOffValue(remainingValue)}
          // onChange={handleChange}
          // onBlur={handleBlur}
          // touched={touched.remaining}
          // error={errors.remaining}
          disabled={!userCanEdit}
          detailsFormField={true}
          readOnly={true}
          customWrapperClass="col-span-3 self-end"
        />

      </div>

      {dueValue > 0 && isOverDue && (<div className="col-span-12 flex justify-between items-center">
        <h2 className="text-field-label-valid text-sm font-semibold leading-6">
          {t('due_threshold_reached_confirm_completion')}
        </h2>
        <div className="w-44">
          <AdminFormFieldSubmit
            type="button"
            variant="success-like"
            label={<span className="flex justify-center whitespace-nowrap">
              {t('mark_as_completed')}
            </span>}
            onClick={() => {
              dispatch(setModalsData({ ...modalsState, showAddMaintenanceDetails: true, maintName: label, maintBucket: maintenanceTypeId }))
            }}
          />
        </div>
      </div>)}
    </>
  )
}
